import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/auth.context';
import { isPublicSessionLive, lastSessionWasPublic } from '../helpers/session-scoped-storage';

/**
 * Whether a `?token=&schema=` handoff owns this load. It bootstraps from a JWT the caller holds
 * (not via `signinRedirect`), so it must not be gated — the logout would discard the session it
 * establishes. Requires both params non-empty, matching the consumers; a stale `?token=` alone
 * is not a handoff and must not disable the gate.
 */
const hasTokenHandoff = (): boolean => {
  try {
    const params = new URLSearchParams(window.location.search);
    return Boolean(params.get('token')) && Boolean(params.get('schema'));
  } catch {
    return false;
  }
};

/**
 * Clears Cognito's surviving session before a login that follows a public-computer one. The tokens
 * died with the browser, but Cognito keeps its own ~60-min cookie and replays it to the next
 * person, so the next login first passes through Cognito's logout to force re-federation.
 *
 * Used by both `Login` and `LoginIdp`, which suppress their own redirect while it fires. Fires once
 * on mount, only when: not authenticated; no `?token=` handoff; the marker is set; and no public
 * session is live in a sibling tab (the shared `myosh_public_active` cookie tells a live sibling
 * from a reopened browser).
 *
 * @param isAuthenticated current auth state from the caller
 * @returns `true` while a logout is in progress — the caller must suppress its redirect
 */
export const usePublicComputerGate = (isAuthenticated: boolean): boolean => {
  const { logout } = useAuth();

  // Decided once, synchronously, from the first render's state. A later dispatch that
  // flips isAuthenticated (the token handoff, say) must not re-evaluate and fire a logout.
  const [isGating, setIsGating] = useState(
    () => !isAuthenticated && !hasTokenHandoff() && lastSessionWasPublic() && !isPublicSessionLive()
  );

  // At most one logout. `isGating` stays true, so a bare guard would let StrictMode's
  // double effect invocation call logout twice.
  const loggedOut = useRef(false);

  useEffect(() => {
    if (!isGating || loggedOut.current) {
      return;
    }
    loggedOut.current = true;
    // Marker NOT cleared here: `logout()` can reject before it navigates, and clearing first
    // would strand Cognito's cookie with no retry signal. Cleared on the completed round trip,
    // in `signoutRedirectCallback`.
    logout().catch(() => {
      // Cognito couldn't be cleared (metadata load failed, say). Stop gating so the page is
      // usable and the caller's login can proceed instead of freezing; the marker stays set, so
      // the next cold start retries.
      loggedOut.current = false;
      setIsGating(false);
    });
  }, [isGating, logout]);

  return isGating;
};
